/**
 * U0 · an ordered set of steps. **One component, two surfaces.**
 *
 *   designer  an operation → its steps
 *   library   an operation type → its sub-operations
 *
 * They are the same shape, so they are the same code. Building them twice is the mistake the
 * product's whole argument is against — *do not duplicate the limit, bind to it* — made in
 * our own source. The drift would be invisible until the two disagreed about something a
 * reviewer was relying on.
 *
 * Carries: `List`/`Flow`, the PRE/EXECUTION/POST bands, the critical stripe, the loop band,
 * and reorder/remove where the surface allows it. What differs between the two surfaces is
 * passed in — never branched on here.
 *
 * **No check logic lives in this file** (D0 §5, the negative grep). Findings arrive already
 * decided; a client that recomputed one would have to invent a reason for it.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DcsChip } from "./steps/CaptureForm";
import { Button, Empty, Flex, Input, Popover, Segmented, Space, Tooltip } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { Activity, Eye, MapPin, ShieldCheck, UserCheck, UserRound, Wrench } from "lucide-react";

import Finding from "./Finding";
import { familyOf } from "./vocabulary";
import Inserter from "./Inserter";
import StepIcon, { STEP_COLOR } from "./StepIcon";
import { useFieldPrefs } from "./fieldPrefs";
import { DEPX, edgePath, layout, sidePath } from "./flowLayout";
import { OUTCOME_LABEL, OUTCOME_TONE } from "./DecisionForm";
import "./surface.css";

const STAGE_LABEL = {
  PRE: "Before starting",
  EXECUTION: "While running",
  POST: "On finishing",
};

const SECOND_PERSON = new Set(["Checked by", "Verified by", "Witnessed by", "CHECKED_BY", "VERIFIED_BY", "WITNESSED_BY"]);

/**
 * A signature is not a footnote on a step — it is the control. A second-person signature is
 * a control only if the record can say who may give it, so a missing policy shows on the row
 * rather than only in a panel (C-3). Behind a panel it is something an author goes looking
 * for, which means they fix it last.
 */
function SignatureTag({ meaning, code, performer, policy }) {
  if (!meaning) return null;
  const second = SECOND_PERSON.has(code || meaning);
  const missing = second && !policy;
  return (
    <Tooltip
      title={
        second
          ? "Must be a different person from the performer. Mechanism, not configuration."
          : "The performer records their own act."
      }
    >
      <span className={`dz-sig ${second ? "second" : "self"} ${missing ? "missing" : ""}`}>
        <span aria-hidden="true">{second ? "✓✓" : "✓"}</span>
        <b>{meaning}</b>
        <span className="w">
          {second ? policy || "no policy — this cannot be enforced" : performer || "the performer"}
        </span>
      </span>
    </Tooltip>
  );
}

// 2026-10-28 (owner) · the second signer on the step's people line, by what the signature means.
// The step holds one second signature: its meaning (`signature`, "Checked by" …) and who may give
// it (`signature_policy`). Shown as it is, never flagged: the step's own form checks it on Save.
const SIGNER = {
  CHECKED_BY: { Icon: UserCheck, cls: "checked", words: "Checked by" },
  VERIFIED_BY: { Icon: ShieldCheck, cls: "verified", words: "Verified by" },
  WITNESSED_BY: { Icon: Eye, cls: "witnessed", words: "Witnessed by" },
};
// 2026-10-28 (owner) · a step's tags hold at most 30 characters; a longer name ends in "..." and
// is read whole on the tag's tooltip.
const TAG_MAX = 30;
const tagText = (t) => { const v = String(t ?? ""); return v.length > TAG_MAX ? `${v.slice(0, TAG_MAX)}...` : v; };
const SIGNER_OF = { "Checked by": "CHECKED_BY", "Verified by": "VERIFIED_BY", "Witnessed by": "WITNESSED_BY" };

function SignerTag({ meaning, code, policy, roleId }) {
  const s = SIGNER[code] || SIGNER[SIGNER_OF[meaning]];
  if (!s) return null;
  // Named as StepForm's save check names it: a role picked (its id) or a signatory's name.
  const named = !!(policy || roleId != null);
  const words = meaning || s.words;
  return (
    <span className={`dz-chip dz-chipi dz-signtag ${s.cls}`}
          title={named ? `${words}: ${policy || "the role chosen on the step"} — a different person from the performer`
                        : `${words}: no role chosen yet`}>
      <s.Icon size={12} aria-label={words} />
      <span className="t">{policy ? tagText(policy) : named ? "role set" : "not set"}</span>
    </span>
  );
}

// 2026-10-28 (owner) · a step says where it runs by its "May run in" alone — the area's name,
// without the cleanroom grade. A grade chip beside it read as the location twice, and an area
// named "Aseptic filling suite (Grade A/B)" carried the grade a second time inside the name.
const GRADE_TEXT = /\s*[(\[]?\s*grade\s+[a-d](\s*\/\s*[a-d])*\b(\s*\(?\s*iso\s*\d+\s*\)?)?\s*[)\]]?/gi;
function placeNames(areas = []) {
  return areas.map((a) => {
    const raw = a.name || a.code || "";
    const bare = raw.replace(GRADE_TEXT, " ").replace(/\s{2,}/g, " ").replace(/^[\s\-–—·,:]+|[\s\-–—·,:]+$/g, "");
    return bare || a.code || raw;
  }).filter(Boolean);
}

// 2026-10-28 (owner) · a step's type as a tag — its icon and its name — in place of the code
// (`PRM-02`) and the upper-case kind beside the title. The code is the tag's tooltip: findings,
// rules and the audit trail still name a step by it.
function sentence(text) {
  const t = String(text || "").replace(/_/g, " ").trim();
  return t === t.toUpperCase() ? t.charAt(0) + t.slice(1).toLowerCase() : t;
}
function TypeTag({ kind, label, code, note }) {
  const text = sentence(label);
  return (
    <span className="dz-type" title={[text, note, code].filter(Boolean).join(" · ")}
          style={STEP_COLOR[kind] ? { "--dz-t": STEP_COLOR[kind] } : undefined}>
      <StepIcon kind={kind} size={12} />
      {text}
    </span>
  );
}

// 2026-10-28 (owner) · continuous monitoring over a step, as one quiet chip — "Monitoring", with a
// count when more than one runs beside it — not an orange `⟳ SPAN-1` per monitoring, which
// crowded the step's name. Pressed, it lists which are attached, by number and name; picking one
// opens it in the panel.
function ContinuousChip({ covered = [], small = false, onOpenSpan }) {
  if (!covered.length) return null;
  const list = (
    <div className="dz-contlist" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
      <div className="h">Continuous monitoring attached</div>
      {covered.map((c) => (
        <button key={c.id} type="button" className="it" disabled={!onOpenSpan}
                onClick={() => onOpenSpan?.(c.id)} title={onOpenSpan ? "Open this monitoring" : undefined}>
          <span className="no">{c.id}</span>
          <span className="nm">{c.name || "—"}</span>
        </button>
      ))}
    </div>
  );
  return (
    <Popover content={list} trigger="click" placement="bottomLeft" rootClassName="dz">
      <button type="button" className={`dz-chip dz-chipi dz-conttag ${small ? "sm" : ""}`}
              onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}
              aria-label={`Monitoring attached: ${covered.map((c) => c.id).join(", ")} — show which`}>
        <Activity size={small ? 11 : 12} aria-hidden="true" />
        Monitoring{covered.length > 1 ? ` · ${covered.length}` : ""}
      </button>
    </Popover>
  );
}

// 2026-10-28 (owner) · the step's "May run in" as one chip, as its machines are: one area by its
// name; several by the first one's name and "+N more", and pressed, the chip lists them all.
// The full names (grade and all) are on the tooltip and in the list.
function PlaceChip({ places = [], full = [] }) {
  if (!places.length) return null;
  const tip = `May run in: ${full.join(", ")}`;
  if (places.length === 1) {
    return (
      <span className="dz-chip dz-chipi dz-placetag" title={tip}>
        <MapPin size={12} aria-hidden="true" />{tagText(places[0])}
      </span>
    );
  }
  const list = (
    <div className="dz-contlist dz-machlist dz-placelist" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
      <div className="h">May run in · {places.length}</div>
      {full.map((name, i) => (
        <div key={`${name}-${i}`} className="it">
          <MapPin size={13} aria-hidden="true" />
          <span className="nm">{name}</span>
        </div>
      ))}
    </div>
  );
  return (
    <Popover content={list} trigger="click" placement="bottomLeft" rootClassName="dz">
      <button type="button" className="dz-chip dz-chipi dz-placetag dz-machtag dz-placemore" title={tip}
              onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}
              aria-label={`${places.length} areas: ${full.join(", ")} — show which`}>
        <MapPin size={12} aria-hidden="true" />{tagText(places[0])}
        <span className="more">+{places.length - 1} more</span>
      </button>
    </Popover>
  );
}

// 2026-10-28 (owner) · the step's machines as one chip. One machine reads by its name; several
// read by the first one's name and "+N more" — five booth names in a row pushed the step to
// three lines — and pressed, the chip lists them all, as the Monitoring chip lists its monitorings. `from` marks equipment
// carried from an earlier step rather than picked on this one.
function MachineChip({ booths = [], from, fromTitle }) {
  if (!booths.length) return null;
  const nameOf = (b) => b.name || b.code;
  const fromText = from ? ` · from step ${from}` : "";
  const cls = `dz-chip dz-chipi ${from ? "carried" : "dz-machine"}`;
  const fromTip = from ? ` — carried from step ${from}${fromTitle ? ` (${fromTitle})` : ""}` : "";
  if (booths.length === 1) {
    return (
      <span className={cls} title={`Equipment: ${nameOf(booths[0])}${fromTip}`}>
        <Wrench size={12} aria-hidden="true" />{tagText(nameOf(booths[0]))}{fromText}
      </span>
    );
  }
  const list = (
    <div className="dz-contlist dz-machlist" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
      <div className="h">Equipment{from ? ` carried from step ${from}` : ""} · {booths.length}</div>
      {booths.map((b) => (
        <div key={b.id ?? b.code} className="it">
          <Wrench size={13} aria-hidden="true" />
          <span className="nm">{nameOf(b)}</span>
        </div>
      ))}
    </div>
  );
  return (
    <Popover content={list} trigger="click" placement="bottomLeft" rootClassName="dz">
      <button type="button" className={`${cls} dz-machtag`}
              onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}
              title={`Equipment: ${booths.map(nameOf).join(", ")}${fromTip}`}
              aria-label={`${booths.length} equipment${fromTip}: ${booths.map(nameOf).join(", ")} — show which`}>
        <Wrench size={12} aria-hidden="true" />{tagText(nameOf(booths[0]))}{fromText}
        <span className="more">+{booths.length - 1} more</span>
      </button>
    </Popover>
  );
}

/**
 * What a node is called, by its own kind.
 *
 * A LANE is named by its `label` — "Within AQL" — and a LOOP by what it repeats over. Neither
 * carries a `title` and neither is a step, so calling them "Untitled step" was wrong twice:
 * it invented a defect that is not one, and it hid the name they do have.
 *
 * "Untitled" belongs to a STEP alone, where it IS a defect — check C-1 blocks on it.
 */
// Family and facts come from the served vocabulary (see vocabulary.js) — the row keeps no
// table of its own.

const clip = (t, n) => (t && t.length > n ? `${t.slice(0, n - 1)}…` : t || "");

function nameOf(node) {
  if (node.kind === "LANE" || node.kind === "HEADING") {
    return { text: node.label || "Unnamed lane", missing: !node.label };
  }
  if (node.kind === "LOOP") {
    const over = node.payload?.over;
    if (node.payload?.kind === "ON_TRIGGER") return { text: `Check row — ${over || "—"}`, missing: !over };
    return { text: over ? `Repeat for each ${over}` : "Repeat — no collection set",
             missing: !over };
  }
  if (node.kind === "DECISION") {
    return { text: node.title || node.label || "Decision", missing: false };
  }
  const t = node.title || node.name;
  return { text: t || "Untitled step", missing: !t };
}

function Row({ step, selected, blocked, finding, critical, actions, onSelect, onOpen,
               onMove, onRemove, vocab = {}, showRole = true, picked = false, onPick, mode = "validate",
               equipment = null, covered = [], onOpenSpan }) {
  const named = nameOf(step);
  // 2026-10-29 (owner) · no "No instruction" chip on the row: a missing instruction is listed in
  // readiness and Review issues, not on every step card.
  // 2026-10-28 (owner) · the tags this viewer switched off (floating toolbar → Show / hide fields).
  const { shown } = useFieldPrefs();
  const places = placeNames(step.areas);
  const rules = step.rules || [];
  const summary = [
    rules.length ? `${rules.length} rule${rules.length === 1 ? "" : "s"}` : null,
    step.instruction ? "instruction" : null,
    step.signature ? "signature" : null,
    (step.attachments || []).length ? `${step.attachments.length} file${step.attachments.length === 1 ? "" : "s"}` : null,
    (step.cpp_edges || []).length ? `CPP → ${step.cpp_edges.map((e) => e.attribute).join(", ")}` : null,
    (step.sops || []).length ? (step.sops || []).map((s) => s.sop_no).join(" · ") : null,
  ].filter(Boolean).join(" · ");
  // The step's own facts, on the row. A limit, a quantity, a frequency — read off the row it
  // was placed from (server-derived `detail`), never composed here. Three at most: a row is
  // for scanning; the inspector is for reading.
  // 2026-10-28 (owner) · a card is its name, where it runs and who does it. The arrow that opened
  // what it uses, its rules, facts, captures and instruction on the card is gone: those are read
  // in the step's panel, one click away, and the list stays a list.

  return (
    <div
      className={`dz-step ${blocked ? "blocked" : ""} ${critical ? "critical" : ""} folded`}
      data-family={familyOf(vocab, step.step_type || step.kind)}
      role="button"
      tabIndex={0}
      aria-current={selected}
      onClick={onSelect}
      onDoubleClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); onOpen?.(); }
        if (e.key === " ") { e.preventDefault(); onSelect?.(); }
      }}
      title="Double-click to open this step"
    >
      <span className="no">{step.number ?? step.position}</span>
      {onPick && step.kind === "STEP" ? (
        /* D10 · select → Repeat. A loop is wrapped around a run of steps, never inserted
           empty; the pick marks the run, the bar at the bottom says what will repeat. */
        <button type="button" className="u0-pick" aria-pressed={picked} title={picked ? "Unpick" : "Pick this step to repeat"}
                onClick={(e) => { e.stopPropagation(); onPick(step); }} />
      ) : null}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="title">
          <b className={named.missing ? "untitled" : ""} title={named.text}>{named.text}</b>
        </span>

        <span className="who">
          {/* 2026-10-14 · continuous monitoring runs beside this step on the floor. 2026-10-28 (owner) ·
              always shown when attached — not one of the tags a viewer can switch off. */}
          <ContinuousChip covered={covered} onOpenSpan={onOpenSpan} />
          {/* 2026-10-30 (owner) · the value is captured from a machine tag. */}
          <DcsChip monitoring={step.monitoring} />
          {/* The area chosen in the step's "May run in" — the room it is worked in. The full
              name, grade and all, stays on the tooltip. */}
          {shown("card.place") ? <PlaceChip places={places} full={(step.areas || []).map((a) => a.name || a.code)} /> : null}
          {/* The equipment chosen for the step — or carried from the equipment step above it. */}
          {/* 2026-10-28 (owner) · one chip however many machines; several are counted and listed on press. */}
          {!shown("card.equipment") ? null : (step.booths || []).length ? (
            <MachineChip booths={step.booths} />
          ) : equipment ? (
            <MachineChip booths={equipment.booths} from={equipment.from} fromTitle={equipment.fromTitle} />
          ) : null}
          {mode === "validate" ? (
            <SignatureTag
              meaning={step.signature}
              code={step.signature_meaning}
              performer={step.role}
              policy={step.signature_policy}
            />
          ) : null}
          {summary && mode === "validate" ? <span className="sum">{summary}</span> : null}
        </span>

        {/* 2026-10-28 (owner) · the step's people, a line of their own under where it runs: who
            performs it, then who signs second — checked, verified or witnessed — each by its own
            icon. A step's role is optional (the operation's "Performed by" says who does the work),
            so it is drawn when the author named one and never flagged when they did not. */}
        <span className="who dz-people">
          {shown("card.performer") && showRole && step.showRole !== false && step.role ? (
            <span className="dz-chip dz-chipi dz-roletag" title={`Performed by: ${step.role}`}>
              <UserRound size={12} aria-label="Performed by" /><span className="t">{tagText(step.role)}</span>
            </span>
          ) : null}
          {shown("card.signer") ? (
            <>
              <SignerTag meaning={step.signature} code={step.signature_meaning} policy={step.signature_policy}
                         roleId={step.signed_by_id ?? step.signature_role_id} />
              {/* 2026-10-29 (owner) · the extra signatures after the first, in the order they are given */}
              {(step.signers || []).map((sg, i) => (
                <SignerTag key={i} meaning={sg.label} code={sg.meaning} policy={sg.role} roleId={sg.role_id} />
              ))}
            </>
          ) : null}
        </span>

        {/* D12 · the SAME component the panel and the queue render. The row is the
            "where", so it shows the "what" and nothing else. */}
        {finding ? <Finding finding={finding} placement="inline" /> : null}
      </span>

      {actions ? (
        <RowActions node={step} onMove={onMove} onRemove={onRemove} />
      ) : null}
    </div>
  );
}

/** 2026-10-13 · a step type by the name the vocabulary gives it ("Dispense Charge"), not its code. */
function typeLabel(vocab, type) {
  if (!type) return "";
  return (vocab?.step_types || []).find((t) => t.value === type)?.label || String(type).replace(/_/g, " ");
}

/** Move up / down / remove — the same three on a step, a repeat and a decision. */
function RowActions({ node, onMove, onRemove, removeTitle = "Remove" }) {
  return (
    <Space size={4} onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
      <Tooltip title="Move up">
        <Button size="small" icon={<ArrowUpOutlined />} disabled={(node.number ?? node.position ?? 1) <= 1} aria-label="Move up"
                onClick={() => onMove?.(node, -1)} />
      </Tooltip>
      <Tooltip title="Move down">
        <Button size="small" icon={<ArrowDownOutlined />} aria-label="Move down" onClick={() => onMove?.(node, 1)} />
      </Tooltip>
      <Tooltip title={removeTitle}>
        <Button size="small" danger icon={<MinusCircleOutlined />} aria-label={removeTitle} onClick={() => onRemove?.(node)} />
      </Tooltip>
    </Space>
  );
}

/** A loop is a band around its body. The body IS the children (D2) — never a range. */
function LoopBand({ node, ...rest }) {
  const p = node.payload || {};
  const sel = rest.selected === node.code;
  return (
    <div className={`dz-loop ${sel ? "sel" : ""}`}>
      {/* The band's head is the loop itself: selecting it opens the repeat's inspector —
          what repeats, what happens at the merge. */}
      <div className="lt" role="button" tabIndex={0} aria-current={sel}
           onClick={(e) => { e.stopPropagation(); rest.onSelect?.(node.code); }}
           onDoubleClick={() => rest.onOpen?.(node.code)}
           onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); rest.onSelect?.(node.code); } }}>
        {(node.number ?? node.position) != null ? <span className="no">{node.number ?? node.position}</span> : null}
        <b>{p.kind === "ON_TRIGGER" ? `Check row — ${p.over || "—"}` : `Repeat for each ${p.over || "—"}`}</b>
        {/* 2026-10-30 (owner) · a check row's trigger: "Every 1,000 units · FM_101.weight_check_no". */}
        {p.kind === "ON_TRIGGER" ? <span className="dz-chip" data-testid="check-row-trigger">{node.check_row?.label || "no trigger"}</span>
         : p.expected ? <span className="dz-chip">{p.expected} expected</span>
         : p.kind === "UNTIL_RULE" && p.ref ? <span className="dz-chip">until {p.ref}</span>
         : p.ref ? <span className="dz-chip" title="The count is derived when the batch is issued; the master record does not carry the number">count from {p.ref} at issue</span>
         : null}
        {rest.concurrency?.stations >= 2 ? (
          <span className="dz-chip par"
                title="Across iterations, parallel. Within one, ordered. Set on the operation.">
            ⫽ up to {rest.concurrency.stations} stations at once
          </span>
        ) : null}
        <span style={{ flex: 1 }} />
        <TypeTag kind="LOOP" label="Repeat" code={node.code} note={p.kind ? sentence(p.kind) : null} />
        {rest.actions ? (
          <RowActions node={node} onMove={rest.onMove} onRemove={rest.onRemove}
                      removeTitle="Remove the repeat and the steps inside it" />
        ) : null}
      </div>
      {/* WHICH materials, and where each is dispensed. The band said what the loop repeats over
          in the record's own words — "each remaining raw material on the bill of materials" —
          and an author could not check that against the four materials it means until a batch
          was issued and somebody was standing at a bench. This is the same list the operator
          picks from, derived once and shown to both. */}
      {(node.subjects || []).length ? (
        <div className="dz-loop-subjects">
          <span className="u0-eyebrow">
            {node.subjects.length} on the bill of materials · one pass each
          </span>
          <ol>
            {node.subjects.map((s) => (
              <li key={s.ref}>
                <b>{s.label}</b>
                <span className="dz-code" style={{ marginLeft: 6 }}>{s.sap_code}</span>
                {s.quantity ? <span> · {s.quantity}</span> : null}
                {s.area ? <span className="u0-rowlist-empty"> · {s.area}</span> : null}
              </li>
            ))}
          </ol>
        </div>
      ) : null}
      <div className="body">
        {(node.children || []).map((c, i) => (
          <React.Fragment key={c.code}>
            <InsertPoint at={c.position ?? i + 1} parent={node.code} source={rest.insertSource}
                         onInsert={rest.onInsert} after={(node.children || [])[i - 1] || null}
                         canInsertAfter={rest.canInsertAfter} />
            <Node node={c} {...rest} vocab={rest.vocab} />
          </React.Fragment>
        ))}
        <InsertPoint
          at={((node.children || []).at(-1)?.position ?? (node.children || []).length) + 1}
          parent={node.code} source={rest.insertSource} onInsert={rest.onInsert}
          after={(node.children || []).at(-1) || null} canInsertAfter={rest.canInsertAfter} />
        {!(node.children || []).length ? (
          <div className="dz-seg" style={{ color: "var(--dz-block)" }}>
            empty — a loop with no body repeats nothing
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * An insertion point, between rows. The mock-up puts adding a step **in the sequence** rather
 * than in a toolbar, and it is right: a step goes somewhere, and a button at the top of the
 * pane makes an author add it and then move it. Revealed on hover, so a sequence reads as a
 * sequence and not as a column of plus signs.
 */
function InsertPoint({ at, parent, source, onInsert, after = null, canInsertAfter }) {
  if (!onInsert) return null;
  // A step is added after one that is finished: the one before it saved and named. Until
  // then this point is shown, disabled, and says what is missing.
  const blockedBy = after && canInsertAfter ? canInsertAfter(after) : null;
  const point = (
    <div className={`u0-ins ${blockedBy ? "off" : ""}`}>
      <span className="ln" />
      <Inserter
        source={source}
        label=""
        icon
        disabled={!!blockedBy}
        onPick={(item) => onInsert({ rank: at, parent, stepType: item.value })}
      />
      <span className="ln" />
    </div>
  );
  return blockedBy ? <Tooltip title={blockedBy}>{point}</Tooltip> : point;
}

/**
 * A decision is a fork: one header, its lanes side by side, and the point where they rejoin.
 * Each lane is named by its condition and holds its own run of steps (D1 §2: a LANE lives in
 * a DECISION and nowhere else). Which lane is taken is settled at execution.
 */
function DecisionBlock({ node, ...rest }) {
  const lanes = (node.children || []).filter((c) => c.kind === "LANE");
  const pick = (code) => (e) => { e.stopPropagation(); rest.onSelect?.(code); };
  return (
    <div className="dz-dec">
      <div className={`dz-dech ${rest.selected === node.code ? "sel" : ""}`} role="button" tabIndex={0}
           onClick={pick(node.code)} onDoubleClick={() => rest.onOpen?.(node.code)}
           onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); rest.onSelect?.(node.code); } }}>
        {(node.number ?? node.position) != null ? <span className="no">{node.number ?? node.position}</span> : null}
        <b className={node.title ? "" : "untitled"}>{node.title || "Decision — what is decided?"}</b>
        <span className="dz-kind">
          {node.depends_on
            ? <>on “{rest.nameOfCode?.[node.depends_on] || node.depends_on}” · {node.basis === "PARAMETER_RECORD" ? "within / outside criteria" : "pass / fail"}</>
            : <>{lanes.length} lanes · one is taken — choose the step it depends on</>}
        </span>
        <span style={{ flex: 1 }} />
        <TypeTag kind="DECISION" label="Decision" code={node.code} />
        {rest.actions ? (
          <RowActions node={node} onMove={rest.onMove} onRemove={rest.onRemove}
                      removeTitle="Remove the decision and its lanes" />
        ) : null}
      </div>
      <div className="dz-lanes" style={{ gridTemplateColumns: `repeat(${Math.max(1, lanes.length)}, minmax(0, 1fr))` }}>
        {lanes.map((lane) => {
          const kids = lane.children || [];
          return (
            <div key={lane.code} className={`dz-lane ${rest.selected === lane.code ? "sel" : ""}`}>
              <div className="dz-laneh" role="button" tabIndex={0} onClick={pick(lane.code)}
                   onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); rest.onSelect?.(lane.code); } }}>
                <span className="dz-code">{lane.code}</span>
                {lane.outcome ? (
                  <span className={`dz-oc oc-${OUTCOME_TONE[lane.outcome] || ""}`}>{OUTCOME_LABEL[lane.outcome] || lane.outcome}</span>
                ) : null}
                <b className={lane.label ? "" : "untitled"}>{lane.label || "Unnamed lane"}</b>
              </div>
              <div className="body">
                {kids.map((c, i) => (
                  <React.Fragment key={c.code}>
                    <InsertPoint at={c.position ?? i + 1} parent={lane.code} source={rest.insertSource}
                                 onInsert={rest.onInsert} after={kids[i - 1] || null}
                                 canInsertAfter={rest.canInsertAfter} />
                    <Node node={c} {...rest} vocab={rest.vocab} />
                  </React.Fragment>
                ))}
                <InsertPoint at={(kids.at(-1)?.position ?? kids.length) + 1} parent={lane.code}
                             source={rest.insertSource} onInsert={rest.onInsert}
                             after={kids.at(-1) || null} canInsertAfter={rest.canInsertAfter} />
                {!kids.length ? (
                  <div className="dz-seg">empty — nothing runs when this lane is taken</div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      <div className="dz-join">the lanes rejoin here</div>
    </div>
  );
}

function Node({ node, ...rest }) {
  if (node.kind === "LOOP") return <LoopBand node={node} {...rest} />;
  if (node.kind === "DECISION") return <DecisionBlock node={node} {...rest} />;
  return (
    <>
      <Row
        step={node}
        selected={rest.selected === node.code}
        blocked={rest.blockedCodes?.has(node.code)}
        finding={rest.findingFor?.(node.code)}
        critical={rest.criticalCodes?.has(node.code)}
        actions={rest.actions}
        onSelect={() => rest.onSelect?.(node.code)}
        onOpen={() => rest.onOpen?.(node.code)}
        onMove={rest.onMove}
        onRemove={rest.onRemove}
        vocab={rest.vocab}
        showRole={rest.showRole}
        picked={rest.picked?.has(node.code)}
        onPick={rest.onPick}
        mode={rest.mode}
        equipment={rest.equipmentAt?.[node.code] || null}
        covered={rest.coveredBy?.[node.code] || []}
        onOpenSpan={rest.onOpenSpan}
      />
      {(node.children || []).map((c) => <Node key={c.code} node={c} {...rest} vocab={rest.vocab} />)}
    </>
  );
}

/** The AVEVA-style flow's sizes: a node's box, and the icon tile at its top. */
const AV_W = 230, AV_H = 150, AV_T = 54;
/** Where a node's name starts under its tile, and the space kept under its last line before an edge. */
const AV_TEXT_Y = AV_T + 10, AV_TEXT_PAD = 4;

/** Flow · laid out, never drawn. Edges come from the sequence, so there is nothing to move. */
function Flow({ steps, blockedCodes, selected, onSelect, vocab = {} }) {
  // 2026-10-15 (owner) · drawn in the AVEVA workflow style: Start → icon tiles named underneath,
  // ◆ decisions with their outcomes on the branches, ⟳ loops in their region → End.
  const L = useMemo(() => layout(steps || [], { nw: AV_W, nh: AV_H, dh: AV_H, terminals: true }), [steps]);
  const [zoom, setZoom] = useState(1);
  // 2026-10-22 (owner) · "attach hona chaiye": an edge leaves a step right under its last line of text,
  // not at the bottom of the box the layout reserves (a step with no equipment or chips left a gap that
  // read as a loose line). The text is measured as drawn, so any number of lines attaches.
  const svgRef = useRef(null);
  const [textH, setTextH] = useState({});
  useLayoutEffect(() => {
    const m = {};
    (svgRef.current?.querySelectorAll("[data-avtext]") || []).forEach((el) => { m[el.dataset.avtext] = el.offsetHeight; });
    if (JSON.stringify(m) !== JSON.stringify(textH)) setTextH(m);
  });
  if (!L.nodes.length) return null;
  const bottomOf = (n) => (n.code && textH[n.code] != null && n.kind !== "join"
    ? { ...n, h: Math.min(n.h, AV_TEXT_Y + textH[n.code] + AV_TEXT_PAD) } : n);

  return (
    <div className="dz-cv">
      <div className="dz-cvport">
        <svg ref={svgRef} width={L.width * zoom} height={L.height * zoom}
             viewBox={`0 0 ${L.width} ${L.height}`} role="img" aria-label="Process flow">
          <defs>
            <marker id="u0arrow" viewBox="0 0 10 10" refX="9" refY="5"
                    markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M0 0 L10 5 L0 10 z" fill="var(--dz-line)" />
            </marker>
          </defs>
          {L.groups.map((g) => (
            <g key={g.code}>
              <rect x={g.x1} y={g.y1} width={g.x2 - g.x1} height={g.y2 - g.y1} rx="10"
                    className="dz-cvgroup" />
              <text x={g.x1 + 12} y={g.y1 + 16} className="dz-cvglabel">
                ⟳ {g.label}{g.count != null ? ` · ${g.count}` : ""}
              </text>
            </g>
          ))}
          {L.edges.map((e, i) => (
            <g key={i}>
              <path d={e.after ? sidePath(e.a, e.b, AV_T) : edgePath(e.back || e.dep ? e.a : bottomOf(e.a), e.b, e.back, e.dep)}
                    className={`dz-cvedge ${e.back ? "back" : ""} ${e.dep && !e.after ? "dep" : ""}`} markerEnd="url(#u0arrow)" />
              {/* 2026-10-22 · a step's own dependency (e.after) is a plain edge, routed beside the steps it skips. */}
              {e.dep && !e.after ? (
                <text x={Math.max(e.a.x + e.a.w / 2, e.b.x + e.b.w / 2) + DEPX + 6}
                      y={(e.a.y + e.a.h / 2 + e.b.y + e.b.h / 2) / 2} className="dz-cvdep">
                  {e.basis === "PARAMETER_RECORD" ? "within / outside" : "pass / fail"}
                </text>
              ) : null}
            </g>
          ))}
          {L.lanes.map((l, i) => (
            <text key={i} x={l.x} y={l.y} className={`dz-cvlane ${l.outcome ? `oc-${OUTCOME_TONE[l.outcome] || ""}` : ""}`}
                  textAnchor="middle">
              [{l.outcome ? `${OUTCOME_LABEL[l.outcome] || l.outcome}${l.label && l.label !== OUTCOME_LABEL[l.outcome] ? ` — ${l.label}` : ""}` : l.label}]
            </text>
          ))}
          {L.nodes.map((n, i) => {
            if (n.kind === "join") {
              return <rect key={i} x={n.x - 6} y={n.y + 2} width="12" height="12" transform={`rotate(45 ${n.x} ${n.y + 8})`}
                           className="dz-avjoin" />;
            }
            if (n.kind === "start" || n.kind === "end") {
              const cx = n.x, cy = n.y + n.h / 2;
              return (
                <g key={i} className={`dz-avterm ${n.kind}`}>
                  <circle cx={cx} cy={cy} r={n.h / 2 - 2} className="o" />
                  {n.kind === "end" ? <circle cx={cx} cy={cy} r={n.h / 2 - 9} className="i" /> : null}
                  <text x={cx + n.h / 2 + 8} y={cy + 4} className="tl">[{n.kind === "start" ? "Start" : "End"}]</text>
                </g>
              );
            }
            const isDec = n.kind === "dec";
            const isLoop = n.kind === "loop";
            const node = n.node || {};
            const label = nameOf(node).text;
            const blocked = blockedCodes?.has(n.code);
            const fam = isDec || isLoop ? "structure" : familyOf(vocab, node.step_type || node.kind);
            const kindLabel = isDec ? "Decision" : isLoop ? "Repeat" : (node.step_type_label || typeLabel(vocab, node.step_type) || "");
            const mid = n.w / 2;
            return (
              <g key={i} transform={`translate(${n.x - n.w / 2}, ${n.y})`}
                 data-family={fam}
                 style={{ "--dz-t": STEP_COLOR[isDec ? "DECISION" : isLoop ? "LOOP" : (node.step_type || node.kind)] }}
                 className={["dz-avnode", isDec && "dec", isLoop && "loop", blocked && "blocked",
                             selected === n.code && "on"].filter(Boolean).join(" ")}
                 onClick={() => n.code && onSelect?.(n.code)} role="button" tabIndex={0}>
                <title>{`${label}${node.instruction ? ` — ${node.instruction}` : ""}`}</title>
                <rect width={n.w} height={n.h} className="hit" />
                {isDec ? (
                  <>
                    <polygon points={`${mid},2 ${mid + AV_T / 2 + 4},${AV_T / 2 + 4} ${mid},${AV_T + 6} ${mid - AV_T / 2 - 4},${AV_T / 2 + 4}`}
                             className="tile" />
                    <text x={mid} y={AV_T / 2 + 10} textAnchor="middle" className="q">?</text>
                  </>
                ) : (
                  <>
                    <rect x={mid - AV_T / 2} y={2} width={AV_T} height={AV_T} rx="14" className="tile" />
                    <g transform={`translate(${mid - 14}, ${2 + AV_T / 2 - 14})`} className="ic">
                      <StepIcon kind={isLoop ? "LOOP" : (node.step_type || node.kind)} size={28} />
                    </g>
                  </>
                )}
                <foreignObject x="0" y={AV_TEXT_Y} width={n.w} height={n.h - AV_TEXT_Y}>
                  <div xmlns="http://www.w3.org/1999/xhtml" className="dz-avtext" data-avtext={n.code || undefined}>
                    <div className="t">{(node.number ?? node.position) != null && !isDec && !isLoop ? `${node.number ?? node.position}. ` : ""}{label}</div>
                    {/* 2026-10-28 (owner) · the flow is the shape of the work: the step and its type,
                        nothing else. Machine, place and monitoring are on the list and the panel. */}
                    <div className="c" title={node.code || undefined}>{kindLabel}</div>
                  </div>
                </foreignObject>
              </g>
            );
          })}
        </svg>
      </div>
      {/* Zoom lives in the corner, over the canvas, where a map keeps it — not in a bar that
          pushes the flow down and says the same thing on every visit. */}
      <div className="dz-cvzoom" role="group" aria-label="Zoom">
        <button onClick={() => setZoom((z) => Math.max(0.4, z / 1.15))} aria-label="Zoom out">−</button>
        <button onClick={() => setZoom(1)} className="z" title="Reset zoom">{Math.round(zoom * 100)}%</button>
        <button onClick={() => setZoom((z) => Math.min(1.6, z * 1.15))} aria-label="Zoom in">+</button>
      </div>
    </div>
  );
}

export default function SequenceView({
  steps = [],
  equipmentAt = {},  // {code: {booths, from}} — equipment carried from the equipment step above
  banded = false,
  view,
  onViewChange,
  selected,
  onSelect,
  blockedCodes,
  criticalCodes,
  findingFor,
  actions = false,
  onMove,
  onRemove,
  emptyNote = "No steps yet. An empty operation is a blank the operator cannot fill.",
  toolbar,
  insertSource,
  onInsert,
  onOpen,
  compact = false,   // rows only — no head, no List | Flow, no bands. Borrowed rows use it.
  vocab = {},        // the served vocabulary: families and limits
  concurrency = null, // the operation's {mode, stations} — a loop band says it
  showRole = true,    // false on a surface that has no roles (the type library)
  onWrap,             // ({codes, over, ref}) → wrap the contiguous run in a LOOP (D10)
  canInsertAfter,     // (step) → null when a step may follow it, else why not (host decides)
  designMode,         // "design" | "validate" (U0 §5) — controlled by the host when given
  onDesignModeChange,
  coveredBy = {},     // 2026-10-14 · {code: [{id, name}]} — the continuous monitoring covering each step
  onOpenSpan,         // (id) => opens that continuous monitoring in the panel, from a step's chip
  readOnly,           // the record cannot be edited — gaps are shown muted. Defaults to !actions.
}) {
  // D10 · the picked steps. All of one parent: picking under another parent starts over,
  // because a loop body is a run of siblings and nothing else.
  const [picked, setPicked] = useState(() => new Set());
  const [pickParent, setPickParent] = useState(null);
  const [over, setOver] = useState("");
  const [ref, setRef] = useState("");
  useEffect(() => { setPicked(new Set()); setPickParent(null); }, [steps]);
  const parentOf = useMemo(() => {
    const out = {};
    const walkP = (list, parent) => (list || []).forEach((n) => { out[n.code] = parent; walkP(n.children, n.code); });
    walkP(steps, null);
    return out;
  }, [steps]);
  const siblingsOf = (parent) => {
    if (parent === null) return steps;
    let found = null;
    const walkS = (list) => (list || []).forEach((n) => { if (n.code === parent) found = n.children || []; else walkS(n.children); });
    walkS(steps);
    return found || [];
  };
  const onPick = onWrap ? (step) => {
    const parent = parentOf[step.code] ?? null;
    setPicked((prev) => {
      const next = parent === pickParent ? new Set(prev) : new Set();
      if (next.has(step.code)) next.delete(step.code); else next.add(step.code);
      return next;
    });
    setPickParent(parent);
  } : undefined;
  const range = useMemo(() => {
    if (!picked.size) return null;
    const sibs = siblingsOf(pickParent);
    const idx = sibs.map((n, i) => (picked.has(n.code) ? i : -1)).filter((i) => i >= 0);
    if (!idx.length) return null;
    const a = Math.min(...idx), b = Math.max(...idx);
    const run = sibs.slice(a, b + 1);
    return { n: idx.length, extra: run.length - idx.length, codes: run.map((n) => n.code),
             structures: run.filter((n) => n.kind !== "STEP").length };
  }, [picked, pickParent, steps]);   // eslint-disable-line react-hooks/exhaustive-deps
  const clearPicks = () => { setPicked(new Set()); setPickParent(null); setOver(""); setRef(""); };
  const [localView, setLocalView] = useState("List");
  const mode = view ?? localView;
  const setMode = onViewChange ?? setLocalView;

  /** PRE / EXECUTION / POST, only where a surface uses stages (the library does). */
  const groups = useMemo(() => {
    if (!banded) return [[null, steps]];
    const order = ["PRE", "EXECUTION", "POST", ""];
    const map = new Map();
    steps.forEach((s) => {
      const key = order.includes(s.stage) ? s.stage : "";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    });
    return order.filter((k) => map.has(k)).map((k) => [k, map.get(k)]);
  }, [steps, banded]);

  // A step named by code elsewhere on a row (what a decision depends on) is shown by its title.
  const nameOfCode = useMemo(() => {
    const out = {};
    const walk = (list) => (list || []).forEach((n) => { if (n.code) out[n.code] = nameOf(n).text; walk(n.children); });
    walk(steps);
    return out;
  }, [steps]);
  const shared = {
    nameOfCode,
    selected, onSelect, onOpen, blockedCodes, criticalCodes, findingFor, actions, onMove,
    onRemove, insertSource, onInsert, concurrency, showRole, picked, onPick: null, mode: designMode || "validate",
    canInsertAfter,
    // What each step is worked on, for the row. Where it runs is the step's own "May run in".
    equipmentAt, coveredBy, onOpenSpan,
    readOnly: readOnly ?? !actions,
  };

  return (
    <Flex vertical style={{ minHeight: 0, width: "100%" }}>
      {compact ? null : (
        <div className="dz-head">
          {toolbar}
          <Space size={6}>
            {onDesignModeChange ? (
              /* U0 §5 · open in Design, enter Validate to prove what was built. Design keeps
                 the rule and the codes; Validate adds the apparatus. */
              <Segmented size="small" value={designMode === "design" ? "Design" : "Validate"}
                         onChange={(v) => onDesignModeChange(v === "Design" ? "design" : "validate")}
                         options={["Design", "Validate"]} />
            ) : null}
            <Segmented size="small" value={mode} onChange={setMode} options={["List", "Flow"]} />
          </Space>
        </div>
      )}

      {!steps.length ? (
        /* Centred in the pane it has to itself. It sat at the top against a tall empty
           space, which reads as content that failed to load rather than as the pane's
           own answer. */
        <div className="dz-empty">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyNote} />
        </div>
      ) : mode === "Flow" ? (
        <Flow steps={steps} blockedCodes={blockedCodes} selected={selected} onSelect={onSelect}
              vocab={vocab} />
      ) : (
        <div className="dz-list">
          {/* 2026-10-14 (owner) · no "N steps selected · Repeat these" bar, and no pick circle on a
              step: a repeat is not made by picking steps on the list. */}
          {groups.map(([stage, rows]) => (
            <div key={stage ?? "all"}>
              {stage !== null ? (
                <div className="dz-band">
                  <span className="s">{stage || "UNSTAGED"}</span>
                  <span className="l">{STAGE_LABEL[stage] || "no stage set"}</span>
                </div>
              ) : null}
              {rows.map((s, i) => (
                <React.Fragment key={s.code || s.id}>
                  <InsertPoint at={s.position ?? i + 1} source={insertSource}
                               onInsert={onInsert} after={rows[i - 1] || null} canInsertAfter={canInsertAfter} />
                  <Node node={s} {...shared} vocab={vocab} />
                </React.Fragment>
              ))}
              <InsertPoint at={(rows[rows.length - 1]?.position ?? rows.length) + 1}
                           source={insertSource} onInsert={onInsert}
                           after={rows[rows.length - 1] || null} canInsertAfter={canInsertAfter} />
            </div>
          ))}
        </div>
      )}
    </Flex>
  );
}
